import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, ShieldCheck, LayoutPanelTop, BookOpen, MessageSquare, KeyRound, TrendingUp, Database, Sparkles, Clock, LayoutDashboard, LineChart, Server, Gauge } from 'lucide-react';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { UserInfo } from '../components/UserInfo';
import { Identity } from '../types/auth';
import { apogeeApiService } from '../services/apogeeApi';
import { KanbanDashboardWidget } from '../components/kanban/KanbanDashboardWidget';
import { PartnerUpdatesWidget } from '../components/partners/PartnerUpdatesWidget';
import { canViewPartnerships } from '../utils/partnershipPermissions';
import { PageHeader } from '../components/ui/PageHeader';
import { SectionHeader } from '../components/ui/SectionHeader';
import { StatTile } from '../components/ui/StatTile';

const ExternalLinkCard = ({ href, icon, label, description }: { href: string, icon: React.ElementType, label: string, description: string }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="glass-card glass-card-interactive p-3 flex items-center gap-3 min-w-0"
    title={href}
  >
    <span className="icon-tile icon-tile-neutral">
      {React.createElement(icon, { className: "w-4 h-4" })}
    </span>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-white truncate">{label}</p>
      <p className="text-xs text-glass-400">{description}</p>
    </div>
    <ExternalLink className="w-4 h-4 text-glass-500 shrink-0" aria-hidden="true" />
  </a>
);

type DashboardViewProps = {
  identity: Identity | null;
  /** Team-scoped staff (builders, event team, newsletter, curators only): no network tooling. */
  teamScoped?: boolean;
}

export function DashboardView({ identity, teamScoped = false }: DashboardViewProps) {
  useDocumentTitle('Dashboard');

  const [apogeeStats, setApogeeStats] = useState<{
    total: number | null;
    recentCount: number | null;
    topGrowthName: string | null;
    topGrowthDelta: number | null;
    loading: boolean;
  }>({ total: null, recentCount: null, topGrowthName: null, topGrowthDelta: null, loading: true });

  useEffect(() => {
    if (teamScoped) return;
    const fetchApogee = async () => {
      try {
        const [packsRes, recentRes, growthRes] = await Promise.allSettled([
          apogeeApiService.listPacks({ limit: 1 }),
          apogeeApiService.getRecentUpdates({ limit: 50 }),
          apogeeApiService.getDownloadGrowth({ limit: 1 }),
        ]);
        setApogeeStats({
          total: packsRes.status === 'fulfilled' ? packsRes.value.total : null,
          recentCount: recentRes.status === 'fulfilled' ? (recentRes.value.items || []).length : null,
          topGrowthName: growthRes.status === 'fulfilled' && (growthRes.value.items || []).length > 0 ? growthRes.value.items[0].name : null,
          topGrowthDelta: growthRes.status === 'fulfilled' && (growthRes.value.items || []).length > 0 ? growthRes.value.items[0].delta_downloads : null,
          loading: false,
        });
      } catch {
        setApogeeStats((prev) => ({ ...prev, loading: false }));
      }
    };
    fetchApogee();
  }, [teamScoped]);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Overview"
        title="Dashboard"
        description="An overview of your network's key statistics."
        icon={LayoutDashboard}
      />

      <UserInfo identity={identity} />

      {/* Kanban Workspaces Widget */}
      <KanbanDashboardWidget userUuid={identity?.user_uuid} />

      {/* Partner updates waiting on PR staff */}
      {(import.meta.env.DEV || canViewPartnerships(identity)) && <PartnerUpdatesWidget />}

      {!teamScoped && (<>
      {/* Apogee Modpack Insights */}
      <section>
        <SectionHeader
          title="Modpack Insights"
          icon={Database}
          as="h2"
          actions={
            <Link to="/modpacks" className="glass-button-sm">
              <ExternalLink className="w-3.5 h-3.5" />
              Open Browser
            </Link>
          }
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
          <StatTile
            label="Tracked Packs"
            value={apogeeStats.total?.toLocaleString() ?? '—'}
            icon={Database}
            tone="accent"
            loading={apogeeStats.loading}
          />
          <StatTile
            label="Updated (7d)"
            value={apogeeStats.recentCount ?? '—'}
            icon={Clock}
            tone="info"
            loading={apogeeStats.loading}
          />
          <StatTile
            label="Top Growth"
            value={apogeeStats.topGrowthDelta !== null ? `+${apogeeStats.topGrowthDelta.toLocaleString()}` : '—'}
            hint={apogeeStats.topGrowthName || undefined}
            icon={TrendingUp}
            tone="success"
            loading={apogeeStats.loading}
            to="/modpacks"
            title={apogeeStats.topGrowthName || 'Top Growth'}
          />
          <StatTile
            label="Modpack Browser"
            value="Browse"
            hint="Explore all packs"
            icon={Sparkles}
            tone="amber"
            to="/modpacks"
            title="Explore All Packs"
          />
        </div>
      </section>

      <section>
        <SectionHeader title="Network" icon={LineChart} as="h2" />
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          <StatTile
            label="StarChart"
            value="Open"
            hint="Players, tick performance and popularity"
            icon={LineChart}
            tone="accent"
            to="/starchart"
            title="StarChart"
          />
          <StatTile
            label="Status Monitor"
            value="Open"
            hint="Live host and instance health"
            icon={Server}
            tone="info"
            to="/status-monitor"
            title="Status Monitor"
          />
          <StatTile
            label="Chunk Throttle"
            value="Open"
            hint="Lag sources and flagged chunks"
            icon={Gauge}
            tone="warning"
            to="/chunk-throttle"
            title="Chunk Throttle"
          />
        </div>
      </section>

      </>)}

      <section>
        <SectionHeader title="External Links" icon={ExternalLink} as="h2" />
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {!teamScoped && (<>
          <ExternalLinkCard 
            href="https://amp.playcdu.co/"
            icon={LayoutPanelTop}
            label="Application Management Panel"
            description="Game panel for server management (restarts, files, etc.)."
          />
          <ExternalLinkCard 
            href="http://qranks.playcdu.co/" 
            icon={ShieldCheck}
            label="Ranks Editor"
            description="Manage player ranks and permissions."
          />
          </>)}
          <ExternalLinkCard
            href="https://outline.playcdu.co/"
            icon={BookOpen}
            label="Staff Documentation"
            description="Internal staff documentation and guides."
          />
          <ExternalLinkCard
            href="http://forum.playcdu.co/"
            icon={MessageSquare}
            label="Community Forum"
            description="Discussions, guides, and community support."
          />
          {!teamScoped && (
          <ExternalLinkCard
            href="http://auth.modded.space/"
            icon={KeyRound}
            label="Authentik SSO"
            description="Single Sign-On authentication service."
          />
          )}
        </div>
      </section>
    </div>
  );
}
